/**
 * ProceduresConstants
 *
 * Mirror of `backend/Libraries/CCMSLib.Models/Procedures/ProceduresConstants.cs`.
 * Seeded lookup IDs (270a_ProceduresManagement_seed_pg.sql). The lookup TABLES are the source of
 * truth for names/colours/icons (see `ProcedureLookupsView`); these constants only name the fixed
 * IDs the engine branches on. IDs start at 1, never 0. Keep additive.
 */
/** The service name API keys must list in AllowedServices. */
export const PROCEDURES_SERVICE_NAME = 'procedures-management';
/** AppRegistry.AppName. AppID is assigned by the database; look it up by name. */
export const PROCEDURES_APP_NAME = 'ProceduresManagement';
/** File Management domain for procedure videos (HLS pipeline + transcripts). RecordId = ProcedureID. */
export const PROCEDURES_FILE_DOMAIN = 'Procedures';
export const PROCEDURES_STATUS = {
    Draft: 1,
    Published: 2,
    Archived: 3,
};
export const PROCEDURES_VISIBILITY = {
    /** Anyone with the QR code or link; no sign-in. Listed in the portal directory when showInDirectory. */
    Public: 1,
    /** Only the people, roles, departments and ministries in the audience (plus admins and collaborators). */
    Private: 2,
};
export const PROCEDURES_AUDIENCE_TYPE = {
    /** targetID = MemberID (works for volunteers who have never signed in). */
    Person: 1,
    /** targetName = role name (AspNetRoles.Name). */
    Role: 2,
    /** targetID = DepartmentID. */
    Department: 3,
    /** targetID = MinistryID. */
    Ministry: 4,
};
export const PROCEDURES_CALLOUT_TYPE = {
    Tip: 1,
    Check: 2,
    Careful: 3,
    Warning: 4,
    Done: 5,
};
/** Callout "type" key (tip | check | careful | warning | done) to its id; unknown keys fall back to Tip. */
export function proceduresCalloutTypeFromKey(key) {
    switch ((key ?? '').trim().toLowerCase()) {
        case 'check':
            return PROCEDURES_CALLOUT_TYPE.Check;
        case 'careful':
            return PROCEDURES_CALLOUT_TYPE.Careful;
        case 'warning':
            return PROCEDURES_CALLOUT_TYPE.Warning;
        case 'done':
            return PROCEDURES_CALLOUT_TYPE.Done;
        default:
            return PROCEDURES_CALLOUT_TYPE.Tip;
    }
}
export const PROCEDURES_MEDIA_TYPE = {
    Image: 1,
    Video: 2,
    Audio: 3,
    Document: 4,
};
export const PROCEDURES_RUN_STATUS = {
    InProgress: 1,
    Completed: 2,
    Abandoned: 3,
};
export const PROCEDURES_RUN_STEP_STATUS = {
    Done: 1,
    Skipped: 2,
    Problem: 3,
};
export const PROCEDURES_RUN_CHANNEL = {
    Public: 1,
    SignedIn: 2,
};
export const PROCEDURES_IMPORT_STATUS = {
    Queued: 1,
    Extracting: 2,
    Structuring: 3,
    Ready: 4,
    Failed: 5,
};
export const PROCEDURES_DOC_INDEX_STATUS = {
    Pending: 1,
    Indexed: 2,
    Failed: 3,
};
export const PROCEDURES_COLLABORATOR_LEVEL = {
    /** May edit the draft and publish. */
    Editor: 1,
    /** Editor plus problem alerts, review reminders and access management. */
    Owner: 2,
};
export const PROCEDURES_CHAT_ROLE = {
    User: 1,
    Assistant: 2,
};
/** AspNetRights names (pg/identity/009). */
export const PROCEDURES_RIGHTS = {
    Read: 'Procedures.Read',
    Manage: 'Procedures.Manage',
    Publish: 'Procedures.Publish',
    Import: 'Procedures.Import',
    RunsRead: 'Procedures.Runs.Read',
    SettingsManage: 'Procedures.Settings.Manage',
};
/** AspNetRoles names (pg/identity/009). */
export const PROCEDURES_ROLES = {
    Admin: 'Admin',
    ProceduresAdmin: 'ProceduresAdmin',
    ProceduresEditor: 'ProceduresEditor',
    /** Every role that may open the console module (rights narrow it further). Comma list as used by [CcmsAuthorize]. */
    ConsoleRoles: 'Admin,ProceduresAdmin,ProceduresEditor',
    /** Roles that see and manage every procedure regardless of collaborators. */
    FullAccessRoles: 'Admin,ProceduresAdmin',
};
/** AIManagement AIFeatureFlag.FeatureType values (seeded off). */
export const PROCEDURES_AI_FEATURES = {
    Import: 'ProcedureImport',
    Chat: 'ProcedureChat',
};
/** Error codes carried in ResponseData.errors[0] (the portal branches on these). */
export const PROCEDURES_ERROR_CODES = {
    /** Anonymous caller asked for a private procedure: show "Sign in with Planning Center". */
    SignInRequired: 'sign_in_required',
    /** Signed in, but not assigned to this private procedure. */
    NotAssigned: 'not_assigned',
    NotPublished: 'not_published',
    PublicRunsDisabled: 'public_runs_disabled',
    RunTokenInvalid: 'run_token_invalid',
    RunClosed: 'run_closed',
    ChatDisabled: 'chat_disabled',
    ChatRateLimited: 'chat_rate_limited',
    AiUnavailable: 'ai_unavailable',
    MediaStorageUnavailable: 'media_storage_unavailable',
    RecaptchaFailed: 'recaptcha_failed',
    UnsupportedFile: 'unsupported_file',
    NothingToImport: 'nothing_to_import',
    CategoryInUse: 'category_in_use',
    QrUnavailable: 'qr_unavailable',
    PublishConflict: 'publish_conflict',
};
/**
 * Header the anonymous portal sends with every public run action: the plain token returned ONCE by
 * `startPublicRun` (`ProcedureRunStartView.runToken`). The PublicProceduresService sets it for you.
 */
export const PROCEDURES_RUN_TOKEN_HEADER = 'X-Run-Token';
//# sourceMappingURL=ProceduresConstants.js.map